import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { useState } from 'react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			Script: {
				select: {
					id: true,
					title: true,
					slug: true,
					server: true,
					description: true,
					updatedAt: true,
					_count: { select: { reactions: true } },
				},
				orderBy: { updatedAt: 'desc' },
			},
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	const mostReactedScript = await prisma.script.findFirst({
		where: { creatorId: owner.id },
		include: { _count: { select: { reactions: true } } },
		orderBy: { reactions: { _count: 'desc' } },
	})

	return json({ owner, mostReactedScript })
}

export default function UserScriptsRoute() {
	const { owner, mostReactedScript } = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const isOwner = user?.id === owner.id
	const ownerDisplayName = owner.name ?? owner.username
	const [serverFilter, setServerFilter] = useState<string | null>(null)

	const servers = [...new Set(owner.Script.map((s) => s.server).filter(Boolean))] as string[]
	const filteredScripts = serverFilter
		? owner.Script.filter((s) => s.server === serverFilter)
		: owner.Script

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Scripts ({owner.Script.length})</h1>
				</div>
				{isOwner && (
					<Link
						to="/script/new"
						className="ml-auto rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
					>
						+ New Script
					</Link>
				)}
			</div>

			{/* Most reacted highlight */}
			{mostReactedScript && mostReactedScript._count.reactions > 0 && (
				<div className="mt-4 rounded-xl border bg-muted/30 p-4 md:mt-6">
					<div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Most Reacted</div>
					<Link to={`/script/${mostReactedScript.slug}`} className="mt-1 block font-semibold hover:underline">
						{mostReactedScript.title}
					</Link>
					<span className="text-sm text-muted-foreground">{mostReactedScript._count.reactions} reactions</span>
				</div>
			)}

			{/* Server filter */}
			{servers.length > 1 && (
				<div className="mt-4 flex flex-wrap gap-2">
					<button
						onClick={() => setServerFilter(null)}
						className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
							serverFilter === null
								? 'border-primary bg-primary/10 text-primary'
								: 'border-border text-muted-foreground hover:bg-muted'
						}`}
					>
						All ({owner.Script.length})
					</button>
					{servers.map((server) => {
						const count = owner.Script.filter((s) => s.server === server).length
						return (
							<button
								key={server}
								onClick={() => setServerFilter(server)}
								className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
									serverFilter === server
										? 'border-primary bg-primary/10 text-primary'
										: 'border-border text-muted-foreground hover:bg-muted'
								}`}
							>
								{server} ({count})
							</button>
						)
					})}
				</div>
			)}

			{/* Scripts grid */}
			<div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{filteredScripts.length === 0 && (
					<p className="col-span-full text-sm text-muted-foreground">No scripts yet.</p>
				)}
				{filteredScripts.map((script) => (
					<Link
						key={script.id}
						to={`/script/${script.slug}`}
						className="group rounded-xl border bg-muted/30 p-4 transition-colors hover:bg-muted/50"
					>
						<div className="flex items-center justify-between">
							{script.server && (
								<span className="rounded-full bg-muted-foreground/10 px-2 py-0.5 text-xs">{script.server}</span>
							)}
							{script._count.reactions > 0 && (
								<span className="text-xs text-muted-foreground">{script._count.reactions} reactions</span>
							)}
						</div>
						<h3 className="mt-2 font-semibold text-sm group-hover:underline md:text-base truncate">{script.title}</h3>
						{script.description && (
							<p className="mt-1 text-xs text-muted-foreground line-clamp-2">{script.description}</p>
						)}
					</Link>
				))}
			</div>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.owner.name ?? params.username
	const count = data?.owner.Script.length ?? 0
	return [
		{ title: `${displayName}'s Scripts | ${brandName}` },
		{ name: 'description', content: `${displayName}'s ${count} script${count !== 1 ? 's' : ''} on ${brandName}` },
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>No user with the username "{params.username}" exists</p>,
			}}
		/>
	)
}
